import { FC, memo, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { Caption, InputNumber } from '@moneyforward/ap-frontend-components';
import { FORMATS } from '@moneyforward/ap-frontend-components/dist/es/atoms/Form/Input/InputNumber';
import classNames from 'classnames';
import { FieldError } from 'react-hook-form';
import styles from './index.module.scss';

export type Props = {
  minValue?: string;
  maxValue?: string;

  minValueErrors?: FieldError;
  maxValueErrors?: FieldError;

  onChangeMinValue: (value: string | number | null) => void;
  onChangeMaxValue: (value: string | number | null) => void;
};

const cx = classNames.bind(styles);

export const NumericRangeInputs: FC<Props> = memo(
  ({
    minValue,
    maxValue,
    minValueErrors,
    maxValueErrors,
    onChangeMinValue,
    onChangeMaxValue,
  }: Props) => {
    const { t } = useTranslation();

    const errorMessages = useMemo(() => {
      const errorMessageSet = new Set<string>();

      if (minValueErrors) {
        errorMessageSet.add(minValueErrors?.message || '');
      }
      if (maxValueErrors) {
        errorMessageSet.add(maxValueErrors?.message || '');
      }

      return Array.from(errorMessageSet);
    }, [minValueErrors, maxValueErrors]);

    return (
      <>
        <div className={cx(styles['input_container'])}>
          <InputNumber
            name='min_value'
            placeholder={t(
              'transaction_items.drawer.component_type.placeholder'
            )}
            onChange={onChangeMinValue}
            value={minValue}
            status={
              minValueErrors || maxValueErrors?.type === 'maxGreaterThanMin'
                ? 'error'
                : undefined
            }
            format={FORMATS.UNIT_WITH_EMPTY}
            stringMode
          />
          <div className={cx(styles['min_max_text_container'])}>~</div>
          <InputNumber
            status={maxValueErrors ? 'error' : undefined}
            name='max_value'
            placeholder={t(
              'transaction_items.drawer.component_type.placeholder'
            )}
            value={maxValue}
            onChange={onChangeMaxValue}
            format={FORMATS.UNIT_WITH_EMPTY}
            stringMode
          />
        </div>
        {errorMessages.length > 0 ? (
          <div>
            {errorMessages.map((error, index) => (
              <Caption type='error' testId='error-msg' key={index}>
                {error}
              </Caption>
            ))}
          </div>
        ) : null}
      </>
    );
  }
);

NumericRangeInputs.displayName = 'NumericRangeInputs';
